import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { FormLabel } from "@/components/ui/form";
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useConfirmDialog } from "@/hooks/use-confirm-dialog";
import { RequiredInput } from "@/components/shared/required-input";
import { UserSelectDropdown } from "@/pages/setting/utils/ignore-price-validations-v2/toolkit/user-select-dropdown";
import { Trash2, Users } from "lucide-react";
import { useMemo, useState } from "react";
import { CustomerSelectionRow } from "./customer-selection-row";

/* eslint-disable @typescript-eslint/no-explicit-any */
export const CustomerSelection = ({
  value,
  sncCustomerList,
  onChange,
}: {
  value: any;
  sncCustomerList: any[];
  onChange: (prop: any) => void;
}) => {
  const { confirm, dialog } = useConfirmDialog();
  const [selectedForDeletion, setSelectedForDeletion] = useState<number[]>([]);
  const valueList = useMemo(() => {
    return Array.isArray(value) ? value : [];
  }, [value]);
  const selectedCustomerData = sncCustomerList?.reduce(
    (acc, cItem) => {
      if (
        valueList?.some(
          (vItem) => String(vItem?.customer_id) === String(cItem?.customer_id),
        )
      ) {
        acc[cItem?.customer_id] = cItem;
      }
      return acc;
    },
    {} as Record<number | string, (typeof sncCustomerList)[number]>,
  );
  const customerSelectionHandle = (prop: any[]) => {
    const currentItem = prop?.map((item: any) => {
      const customerId = item?.customer_id ?? item;
      const customer = sncCustomerList?.find(
        (cItem: any) => String(cItem?.customer_id) === String(customerId),
      );
      const branchIds = Array.isArray(item?.branch_id) ? item.branch_id : [];
      const branches = branchIds.map((branchId: string | number) => {
        const branch = customer?.branch_list?.find(
          (bItem: any) => String(bItem?.branch_id) === String(branchId),
        );
        return { id: branchId, name: branch?.branch_name ?? branchId };
      });
      return {
        customer_id: customerId,
        customer_name:
          customer?.customer_name ?? customer?.display_name ?? customerId,
        branches,
      };
    });
    onChange([...currentItem, ...valueList]);
  };

  const selectedBranchesMap = useMemo(() => {
    return valueList.reduce(
      (acc, item) => {
        acc[item?.customer_id] = Array.isArray(item?.branches)
          ? item.branches.map((branch: any) => branch?.id)
          : [];
        return acc;
      },
      {} as Record<string | number, (string | number)[]>,
    );
  }, [valueList]);

  const handleToggleForDeletion = (index: number) => {
    setSelectedForDeletion((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  const isAllSelected =
    valueList.length !== 0 && selectedForDeletion.length === valueList.length;

  const toggleSelectAllForDeletion = () => {
    setSelectedForDeletion(
      isAllSelected ? [] : valueList.map((_item, index) => index),
    );
  };

  const removeSelectedCustomers = () => {
    onChange(
      valueList.filter((_item, index) => !selectedForDeletion.includes(index)),
    );
    setSelectedForDeletion([]);
  };

  const handleBranchChange = (
    index: number,
    branchIds: (string | number)[],
  ) => {
    const item = valueList[index];
    const customer = selectedCustomerData?.[item?.customer_id];
    const branches = branchIds.map((branchId) => {
      const branch = customer?.branch_list?.find(
        (bItem: any) => String(bItem?.branch_id) === String(branchId),
      );
      return { id: branchId, name: branch?.branch_name ?? branchId };
    });
    onChange(
      valueList.map((it, i) => (i === index ? { ...it, branches } : it)),
    );
  };

  const handleDeleteClick = () => {
    confirm({
      title: "Delete Customers",
      description: "Are you sure you want to delete the selected customers?",
    }).then((res) => {
      if (res) {
        removeSelectedCustomers();
      }
    });
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <FormLabel>Customers</FormLabel>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="destructive"
            className="h-[37px] w-[37px]"
            onClick={handleDeleteClick}
            disabled={selectedForDeletion.length === 0}
          >
            <Trash2 size={16} />
          </Button>
          <UserSelectDropdown
            list={sncCustomerList || []}
            onChange={customerSelectionHandle}
            selectedValue={valueList?.map((item) => item?.customer_id) ?? []}
            // selectedValue={value ? Object.keys(value) : []}
            branchListKey="branch_list"
            branchBindValue="branch_id"
            branchBindLabel="branch_name"
            selectedBranchesMap={selectedBranchesMap}
          >
            <Button type="button" variant={"action"} className="h-auto">
              Add Customer
            </Button>
          </UserSelectDropdown>
        </div>
      </div>
      <div className="w-full mt-2 rounded-md border max-h-[400px] overflow-y-auto overflow-x-hidden">
        {valueList.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
            <div className="flex items-center justify-center h-10 w-10 rounded-full bg-muted">
              <Users size={18} className="text-muted-foreground" />
            </div>
            <p className="text-sm font-medium text-gray-700">
              No customers added
            </p>
            <p className="text-xs text-muted-foreground">
              Click &quot;Add Customer&quot; to map this UOM to specific
              customers.
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40px]">
                  <Checkbox
                    checked={isAllSelected}
                    onCheckedChange={toggleSelectAllForDeletion}
                    disabled={valueList.length === 0}
                  />
                </TableHead>
                <TableHead className="w-[60px]">Sr</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead className="w-[40%]">
                  Branch
                  <RequiredInput />
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {valueList?.map((item, index) => {
                return (
                  <CustomerSelectionRow
                    item={item}
                    key={index}
                    data={selectedCustomerData?.[item?.customer_id]}
                    onChange={onChange}
                    index={index}
                    checked={selectedForDeletion.includes(index)}
                    onToggleForDeletion={() => handleToggleForDeletion(index)}
                    onBranchChange={(branchId: (string | number)[]) =>
                      handleBranchChange(index, branchId)
                    }
                  />
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>
      {dialog}
    </div>
  );
};
